import { withSelectedCompany } from '@/modules/shared/hocs/withSelectedCompany'
import { useShallow } from 'zustand/shallow'
import * as React from 'react'
import { createContext, useContext, useMemo } from 'react'
import { showMessage as showMessageAction } from '@/modules/shared/stores/messageStore'
import { TaskType } from '@/app/constants/taskType'
import type { MessageModel } from '@/app/models/messageModel'
import { useRouteCompanyId } from '@/modules/shared/hooks/useRouteCompanyId'
import {
  selectCompletedTasks,
  selectOwnRunningTasksOf,
  useTaskTrackerStore,
  type LegacyTaskModel,
} from '@/modules/shared/stores/taskTrackerStore'

/**
 * Task registration for the ~20 screens that start long-running work.
 *
 * The store behind `addTask` moved from a non-persisted redux slice to
 * `taskTrackerStore`, which survives a refresh and is reconciled against the server.
 * The context API is unchanged on purpose: every call site keeps working, and the
 * risky part of the change stays in one place.
 */

interface TaskContextType {
  addTask: (id: string, type: TaskType, data: any) => void
  showMessage: (message: MessageModel) => void
  tasks: LegacyTaskModel[]
  completedTasks: LegacyTaskModel[]
}

const TaskContext = createContext<TaskContextType | null>(null)

interface TaskProviderOwnProps {
  children: React.ReactNode
}

interface TaskProviderStateProps {
  companyId: string
}

type TaskProviderProps = TaskProviderOwnProps & TaskProviderStateProps

const TaskProviderComponent: React.FC<TaskProviderProps> = ({
  children,
  companyId: selectedCompanyId,
}) => {
  // The URL wins: the legacy redux selection is empty on most modern routes, and a
  // task registered with a blank company id would never match what the sync loop
  // asks the server for — it would simply never resolve.
  const routeCompanyId = useRouteCompanyId()
  const companyId = routeCompanyId || selectedCompanyId

  const track = useTaskTrackerStore((s) => s.track)
  const tasks = useTaskTrackerStore(useShallow(selectOwnRunningTasksOf(companyId)))
  const completedTasks = useTaskTrackerStore(useShallow(selectCompletedTasks))

  const addTask = React.useCallback(
    (id: string, type: TaskType, data: any) => {
      track({ id, companyId, type, data })
    },
    [track, companyId],
  )

  const showMessage = React.useCallback((message: MessageModel) => {
    showMessageAction(message)
  }, [])

  const value = useMemo(
    () => ({
      addTask,
      showMessage,
      tasks,
      completedTasks,
    }),
    [addTask, showMessage, tasks, completedTasks],
  )

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>
}

export const TaskProvider = withSelectedCompany(
  TaskProviderComponent as any,
) as unknown as React.ComponentType<TaskProviderOwnProps>

export const useTask = () => {
  const ctx = useContext(TaskContext)
  if (!ctx) {
    throw new Error('useTask must be used inside <TaskProvider>')
  }
  return ctx
}
